// src/components/Register.jsx

import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updatePassword,
} from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [confirmPassword, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isInviteLink = isSignInWithEmailLink(auth, window.location.href);
  const orgId = params.get("orgId");

  // Si es invitación, prellenamos email desde localStorage (si existe)
  useEffect(() => {
    if (!isInviteLink) return;

    // 1) primero, el e-mail que viene en la URL (nuevo)
    const emailFromURL = params.get("email");
    if (emailFromURL) {
      setEmail(decodeURIComponent(emailFromURL));
      return;
    }

    // 2) si no venía en la URL, usa el respaldo local (mismo dispositivo)
    const stored = localStorage.getItem("emailForSignIn");
    if (stored) setEmail(stored);
  }, [isInviteLink, params]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      return Swal.fire("Oops...", "Todos los campos son obligatorios", "error");
    }
    if (password.length < 6) {
      return Swal.fire(
        "Oops...",
        "La contraseña debe tener al menos 6 caracteres",
        "error",
      );
    }
    if (password !== confirmPassword) {
      return Swal.fire("Oops...", "Las contraseñas no coinciden", "error");
    }

    setLoading(true);

    const emailNorm = email.trim().toLowerCase();
    const displayName = `${firstName.trim().split(" ")[0]} ${
      lastName.trim().split(" ")[0]
    }`;

    try {
      // ===== FLUJO A: Invitación (NUEVO MODELO) =====
      if (isInviteLink && orgId) {
        // 1) Consumir link
        try {
          await signInWithEmailLink(auth, emailNorm, window.location.href);
        } catch (err) {
          console.error("Error al consumir el link:", err);
          await Swal.fire(
            "Oops...",
            "El enlace de invitación no es válido o expiró.",
            "error",
          );
          return;
        }

        // 2) Set contraseña + nombre
        await updatePassword(auth.currentUser, password);
        await updateProfile(auth.currentUser, { displayName });

        const uid = auth.currentUser.uid;

        // 3) Validar invitación (orgs/{orgId}/invitations/{emailLower})
        const invRef = doc(db, "orgs", orgId, "invitations", emailNorm);
        const invSnap = await getDoc(invRef);

        if (!invSnap.exists()) {
          await Swal.fire(
            "Error",
            "La invitación ya no existe o fue eliminada.",
            "error",
          );
          return;
        }

        const invData = invSnap.data() || {};
        if (invData.status && invData.status !== "pending") {
          await Swal.fire(
            "Error",
            "Esta invitación ya fue utilizada o ya no está activa.",
            "error",
          );
          return;
        }

        const finalRol = invData.role || invData.rol || "Ventas";

        // 4) Perfil global users/{uid} (createdAt solo si no existe)
        const userRef = doc(db, "users", uid);
        const userSnap = await getDoc(userRef);
        const createdAtField = userSnap.exists()
          ? {}
          : { createdAt: serverTimestamp() };

        await setDoc(
          userRef,
          {
            emailLower: emailNorm,
            email: emailNorm,
            nombre: firstName,
            apellido: lastName,
            defaultOrgId: orgId,
            lastLoginAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            ...createdAtField,
          },
          { merge: true },
        );

        // 5) Member en la org orgs/{orgId}/members/{uid}
        const memberRef = doc(db, "orgs", orgId, "members", uid);
        const memberSnap = await getDoc(memberRef);
        const memberCreatedAtField = memberSnap.exists()
          ? {}
          : { createdAt: serverTimestamp() };

        await setDoc(
          memberRef,
          {
            uid,
            role: finalRol,
            status: "active",
            invitedEmail: emailNorm,
            updatedAt: serverTimestamp(),
            ...memberCreatedAtField,
          },
          { merge: true },
        );

        // 6) Índice usuario -> org users/{uid}/orgs/{orgId}
        const uOrgRef = doc(db, "users", uid, "orgs", orgId);
        const uOrgSnap = await getDoc(uOrgRef);
        const uOrgCreatedAtField = uOrgSnap.exists()
          ? {}
          : { createdAt: serverTimestamp() };

        await setDoc(
          uOrgRef,
          {
            orgId,
            role: finalRol,
            status: "active",
            updatedAt: serverTimestamp(),
            ...uOrgCreatedAtField,
          },
          { merge: true },
        );

        // 7) Marcar invitación usada
        await updateDoc(invRef, {
          status: "used",
          usedAt: serverTimestamp(),
          usedByUid: uid,
        });

        // 8) Limpieza
        localStorage.removeItem("inviteOrgId");
        localStorage.removeItem("emailForSignIn");

        navigate("/registro-exitoso");
        return;
      }

      // ===== FLUJO B: Registro normal (NUEVO MODELO) =====
      const { user: fbUser } = await createUserWithEmailAndPassword(
        auth,
        emailNorm,
        password,
      );
      await updateProfile(fbUser, { displayName });
      await sendEmailVerification(fbUser);

      const uid = fbUser.uid;

      // 1) Perfil global users/{uid}
      const userRef = doc(db, "users", uid);
      await setDoc(
        userRef,
        {
          emailLower: emailNorm,
          email: emailNorm,
          nombre: firstName,
          apellido: lastName,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
        },
        { merge: true },
      );

      // 2) Crear org nueva
      const newOrgRef = doc(collection(db, "orgs"));
      await setDoc(newOrgRef, {
        name: "Mi Empresa",
        ownerUid: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 3) Member admin
      await setDoc(
        doc(db, "orgs", newOrgRef.id, "members", uid),
        {
          uid,
          role: "Administrador",
          status: "active",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      // 4) Índice usuario -> org
      await setDoc(
        doc(db, "users", uid, "orgs", newOrgRef.id),
        {
          orgId: newOrgRef.id,
          role: "Administrador",
          status: "active",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      // 5) defaultOrgId
      await updateDoc(userRef, {
        defaultOrgId: newOrgRef.id,
        updatedAt: serverTimestamp(),
      });

      await Swal.fire(
        "¡Cuenta creada!",
        "Revisa tu correo para verificar la cuenta.",
        "success",
      );

      localStorage.removeItem("inviteOrgId");
      localStorage.removeItem("emailForSignIn");

      navigate("/");
    } catch (err) {
      console.error("Error registrando usuario:", err);
      await Swal.fire(
        "Oops...",
        err.code === "auth/email-already-in-use"
          ? "Este correo ya está en uso."
          : err.message || "Hubo un error, inténtalo de nuevo.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-form">
        <h1>Regístrate</h1>
        {isInviteLink && orgId ? (
          <p style={{ color: "#555" }}>
            Crea tu contraseña y completa tu perfil para unirte al equipo.
          </p>
        ) : (
          <p style={{ color: "#555" }}>
            Completa los siguientes campos para crear tu cuenta.
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <input
              type="text"
              placeholder="Nombre(s)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading}
              style={{ flex: 1 }}
            />
            <input
              type="text"
              placeholder="Apellido(s)"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading}
              style={{ flex: 1 }}
            />
          </div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading} // sólo deshabilitamos al enviar
          />
          <input
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Repetir Contraseña"
            value={confirmPassword}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading}
            style={{ borderRadius: 30, marginTop: 10 }}
          >
            {loading ? "Procesando…" : "Continuar"}
          </button>
        </form>
        <div className="register-containerLogin">
          <p style={{ display: "inline-block", marginRight: "5px" }}>
            ¿Ya tienes una cuenta?
          </p>
          <span onClick={() => navigate("/login")} className="register-link">
            Login
          </span>
        </div>
      </div>
    </div>
  );
};

export default Register;
